import { useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export interface UserNotification {
  id: string;
  user_id: string;
  type: "achievement" | "blog_article" | "system";
  title: string;
  message: string | null;
  icon: string | null;
  reference_id: string | null;
  reference_url: string | null;
  is_read: boolean;
  created_at: string;
}

async function fetchNotificationsData(userId: string): Promise<UserNotification[]> {
  const { data, error } = await supabase
    .from("user_notifications")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(50);

  if (error) throw error;
  return (data as UserNotification[]) || [];
}

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: notifications = [], isLoading: loading, error } = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: () => fetchNotificationsData(user!.id),
    enabled: !!user?.id,
    staleTime: 30 * 1000,
  });

  // Realtime subscription — invalidates query on any change
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`notifications-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "user_notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotification = payload.new as UserNotification;

          queryClient.setQueryData<UserNotification[]>(
            ["notifications", user.id],
            (prev = []) => [newNotification, ...prev]
          );

          if (newNotification.reference_id?.startsWith("expiry_")) {
            const isUrgent =
              newNotification.reference_id.includes("_urgent") ||
              newNotification.reference_id.includes("_30");

            if (isUrgent) {
              toast.warning(newNotification.title, {
                description: newNotification.message || undefined,
                duration: 8000,
                action: {
                  label: "Ver",
                  onClick: () =>
                    (window.location.href =
                      newNotification.reference_url ||
                      "/meu-armario?tab=inventario"),
                },
              });
            } else {
              toast.info(newNotification.title, {
                description: newNotification.message || undefined,
                duration: 6000,
              });
            }
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "user_notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const updated = payload.new as UserNotification;
          queryClient.setQueryData<UserNotification[]>(
            ["notifications", user.id],
            (prev = []) => prev.map((n) => (n.id === updated.id ? updated : n))
          );
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "user_notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const deleted = payload.old as { id: string };
          queryClient.setQueryData<UserNotification[]>(
            ["notifications", user.id],
            (prev = []) => prev.filter((n) => n.id !== deleted.id)
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  const markAsRead = useCallback(
    async (id: string) => {
      if (!user) return;

      queryClient.setQueryData<UserNotification[]>(
        ["notifications", user.id],
        (prev = []) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );

      const { error } = await supabase
        .from("user_notifications")
        .update({ is_read: true })
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) {
        console.error("Error marking notification as read:", error);
        queryClient.invalidateQueries({ queryKey: ["notifications", user.id] });
      }
    },
    [user, queryClient]
  );

  const markAllAsRead = useCallback(
    async () => {
      if (!user) return;

      queryClient.setQueryData<UserNotification[]>(
        ["notifications", user.id],
        (prev = []) => prev.map((n) => ({ ...n, is_read: true }))
      );

      const { error } = await supabase
        .from("user_notifications")
        .update({ is_read: true })
        .eq("user_id", user.id)
        .eq("is_read", false);

      if (error) {
        console.error("Error marking all notifications as read:", error);
        queryClient.invalidateQueries({ queryKey: ["notifications", user.id] });
      }
    },
    [user, queryClient]
  );

  const deleteNotification = useCallback(
    async (id: string) => {
      if (!user) return;

      queryClient.setQueryData<UserNotification[]>(
        ["notifications", user.id],
        (prev = []) => prev.filter((n) => n.id !== id)
      );

      const { error } = await supabase
        .from("user_notifications")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) {
        console.error("Error deleting notification:", error);
        queryClient.invalidateQueries({ queryKey: ["notifications", user.id] });
      }
    },
    [user, queryClient]
  );

  const clearAllNotifications = useCallback(
    async () => {
      if (!user) return;

      queryClient.setQueryData<UserNotification[]>(["notifications", user.id], []);

      const { error } = await supabase
        .from("user_notifications")
        .delete()
        .eq("user_id", user.id);

      if (error) {
        console.error("Error clearing notifications:", error);
        queryClient.invalidateQueries({ queryKey: ["notifications", user.id] });
      }
    },
    [user, queryClient]
  );

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return {
    notifications,
    unreadCount,
    loading,
    error: error as Error | null,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    refetch: () => queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] }),
  };
}
